/**
 * /whatsapp/instancias — Lista de instancias WhatsApp.
 *
 * Cada instancia = 1 numero fisico vinculado a uma sessao WA Web.
 * KPIs por status; tabela com acoes (detalhar, desconectar, excluir).
 * Polling 15s para refletir mudancas de status que vem via webhook do Node.
 */
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiPlus, FiRefreshCw, FiSmartphone, FiTrash2 } from "react-icons/fi";

import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import useWhatsappPolling from "../../../hooks/useWhatsappPolling";
import {
  criarInstancia,
  excluirInstancia,
  iniciarSessaoInstancia,
  listarInstancias,
  STATUS_INSTANCIA_LABEL,
  STATUS_INSTANCIA_TONE,
} from "../../../services/whatsapp";
import { PageContainer } from "../../../styles/shared";
import { fmtData } from "../../Orcamentos/helpers";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Field,
  FieldLabel,
  FilterBar,
  Form,
  FormFooter,
  IconButton,
  InlineHint,
  Input,
  Pill,
  SecondaryButton,
  SectionTitle,
  Table,
  TableWrapper,
} from "../../Orcamentos/styles";
import SlideOverPanel from "../../../components/SlideOverPanel";

const STATUS_ORDER = [
  "DESCONECTADA",
  "AGUARDANDO_QR",
  "CONECTADA",
  "EXPIRADA",
  "BANIDA",
];

export default function InstanciasLista() {
  const navigate = useNavigate();
  const { request, hasPermission } = useAuth();
  const podeGerenciar = hasPermission("whatsapp:manage");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState("");

  const [novoOpen, setNovoOpen] = useState(false);
  const [novoNome, setNovoNome] = useState("");
  const [novoErro, setNovoErro] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await listarInstancias(request, {
        status: filtroStatus || undefined,
        pageSize: 100,
      });
      const lista = Array.isArray(payload) ? payload : payload?.results || [];
      setItems(lista);
    } catch (err) {
      setError(err?.message || "Erro ao carregar instancias.");
    } finally {
      setLoading(false);
    }
  }, [request, filtroStatus]);

  useWhatsappPolling(carregar, 15000, [carregar]);

  const contagens = useMemo(() => {
    const acc = { total: items.length };
    STATUS_ORDER.forEach((s) => {
      acc[s] = items.filter((i) => i.status === s).length;
    });
    return acc;
  }, [items]);

  const kpis = useMemo(
    () => [
      { label: "Total", value: String(contagens.total) },
      { label: "Conectadas", value: String(contagens.CONECTADA || 0), tone: "success" },
      {
        label: "Aguardando QR",
        value: String(contagens.AGUARDANDO_QR || 0),
        tone: "info",
      },
      {
        label: "Desconectadas",
        value: String(contagens.DESCONECTADA || 0),
        tone: "neutral",
      },
      {
        label: "Banidas",
        value: String(contagens.BANIDA || 0),
        tone: "danger",
      },
    ],
    [contagens],
  );

  const handleCriar = async () => {
    setNovoErro(null);
    if (!novoNome.trim()) {
      setNovoErro("Nome obrigatório.");
      return;
    }
    if (novoNome.trim().length > 80) {
      setNovoErro("Nome muito longo (max 80).");
      return;
    }
    try {
      const nova = await criarInstancia(request, { nome_interno: novoNome.trim() });
      setNovoOpen(false);
      setNovoNome("");
      // Imediato: abre o detalhe pra ja iniciar sessao + escanear QR
      navigate(`/whatsapp/instancias/${nova.id}`);
    } catch (err) {
      setNovoErro(err?.message || "Erro ao criar instancia.");
    }
  };

  const handleIniciarSessao = async (id) => {
    await iniciarSessaoInstancia(request, id);
    navigate(`/whatsapp/instancias/${id}`);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Excluir esta instância? Sessão do WhatsApp será desconectada.")) {
      return;
    }
    await excluirInstancia(request, id);
    await carregar();
  };

  return (
    <PageContainer>
      <EntityHeader
        title="Instancias WhatsApp"
        subtitle="Cada número físico vinculado ao PharuERP via whatsapp-web.js"
        primaryAction={
          podeGerenciar
            ? {
                label: "Nova instância",
                icon: <FiPlus />,
                onClick: () => setNovoOpen(true),
              }
            : null
        }
        secondaryActions={[
          {
            label: "Atualizar",
            icon: <FiRefreshCw />,
            onClick: () => carregar(),
          },
        ]}
      />

      <KpiStrip items={kpis} />

      <Card>
        <SectionTitle>Lista</SectionTitle>
        <FilterBar>
          <Field>
            <FieldLabel>Status</FieldLabel>
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              style={{ padding: "6px 10px", borderRadius: 6 }}
            >
              <option value="">Todos</option>
              {STATUS_ORDER.map((s) => (
                <option key={s} value={s}>
                  {STATUS_INSTANCIA_LABEL[s] || s}
                </option>
              ))}
            </select>
          </Field>
        </FilterBar>

        {error && <ErrorMsg>{error}</ErrorMsg>}
        {!error && items.length === 0 && !loading && (
          <EmptyMsg>
            Nenhuma instância ainda. Clique em "Nova instância" para conectar
            seu primeiro número.
          </EmptyMsg>
        )}

        {items.length > 0 && (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Número</th>
                  <th>Status</th>
                  <th>Última atividade</th>
                  <th style={{ width: 160 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((inst) => (
                  <tr
                    key={inst.id}
                    onClick={() => navigate(`/whatsapp/instancias/${inst.id}`)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <FiSmartphone style={{ marginRight: 6, verticalAlign: "middle" }} />
                      {inst.nome_interno}
                      {inst.envio_pausado && (
                        <Pill $tone="warning" style={{ marginLeft: 8 }}>
                          Pausada
                        </Pill>
                      )}
                    </td>
                    <td>{inst.phone_number || <InlineHint>—</InlineHint>}</td>
                    <td>
                      <Pill $tone={STATUS_INSTANCIA_TONE[inst.status] || "neutral"}>
                        {STATUS_INSTANCIA_LABEL[inst.status] || inst.status}
                      </Pill>
                    </td>
                    <td>{fmtData(inst.ultimo_evento_em) || "—"}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: "flex", gap: 6 }}>
                        {podeGerenciar && (inst.status === "DESCONECTADA" || inst.status === "EXPIRADA") && (
                          <AsyncButton
                            variant="secondary"
                            onClick={() => handleIniciarSessao(inst.id)}
                            successMessage="Iniciando sessão..."
                            errorMessage="Falha ao iniciar sessão"
                          >
                            Conectar
                          </AsyncButton>
                        )}
                        {podeGerenciar && (
                          <IconButton
                            type="button"
                            title="Excluir"
                            onClick={() => handleExcluir(inst.id)}
                          >
                            <FiTrash2 />
                          </IconButton>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>

      <SlideOverPanel
        open={novoOpen}
        onClose={() => {
          setNovoOpen(false);
          setNovoErro(null);
          setNovoNome("");
        }}
        title="Nova instância WhatsApp"
        subtitle="Cadastra o número. Depois você escaneia o QR pra ativar."
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            handleCriar();
          }}
        >
          <Field>
            <FieldLabel>Nome interno</FieldLabel>
            <Input
              type="text"
              maxLength={80}
              value={novoNome}
              onChange={(e) => setNovoNome(e.target.value)}
              placeholder="Ex.: Atendimento comercial"
              autoFocus
            />
            <InlineHint>
              Identificação usada na sidebar e no audit log. Só você e a equipe veem.
            </InlineHint>
          </Field>

          {novoErro && <ErrorMsg>{novoErro}</ErrorMsg>}

          <FormFooter>
            <SecondaryButton
              type="button"
              onClick={() => setNovoOpen(false)}
            >
              Cancelar
            </SecondaryButton>
            <AsyncButton
              variant="primary"
              onClick={handleCriar}
              successMessage="Instância criada"
              errorMessage="Falha ao criar instância"
              silentError
            >
              Criar e ir para detalhe
            </AsyncButton>
          </FormFooter>
        </Form>
      </SlideOverPanel>
    </PageContainer>
  );
}
